import {
  type CreateOneRequestType,
  type GetListResponseType,
  type GetOneRequestType,
  type UpdateOneRequestType,
  type ResourceAdapterType,
  type ResourceAdapterToAdapterQueryKeysType,
  fetchAndCollate,
  Resources,
  type DeleteOneRequestType,
  type DeleteOneResponseType,
  type UpdateOneResponseType,
  type CreateOneResponseType,
  type GetListRequestType,
  type GetOneResponseType,
} from "../util/index.ts";
import { restAPI } from "./api.ts";
import {
  type EventCategoryGetListRequestType,
  type EventCategoryType,
} from "../schemas/EventCategorySchemas.ts";

export interface EventCategoryAdapterType
  extends Pick<
    ResourceAdapterType<EventCategoryType>,
    "getOne" | "getList" | "createOne" | "updateOne" | "deleteOne"
  > {
  getOne: (
    params: GetOneRequestType
  ) => Promise<GetOneResponseType<EventCategoryType>>;
  getList: (
    params: GetListRequestType
  ) => Promise<GetListResponseType<EventCategoryType>>;
  createOne: (
    params: CreateOneRequestType<EventCategoryType>
  ) => Promise<CreateOneResponseType<EventCategoryType>>;
  updateOne: (
    params: UpdateOneRequestType<EventCategoryType>
  ) => Promise<UpdateOneResponseType<EventCategoryType>>;
  deleteOne: (
    params: DeleteOneRequestType<EventCategoryType>
  ) => Promise<DeleteOneResponseType>;
}

const endpoint = "/event_categories";

export const EventCategoryAdapter: EventCategoryAdapterType = {
  /**
   * Get Event Category
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/event_categories/get_event_category
   */
  getOne: ({ id }: GetOneRequestType) =>
    restAPI.nodeAPI
      .GET<EventCategoryType>({ endpoint: `${endpoint}/${id}` })
      .then((data) => ({ data })),

  /**
   * List Event Categories
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/event_categories/list_event_categories
   */
  getList: ({
    page,
    pageSize,
    sortBy = "name",
    sortDirection = "asc",
    filters,
  }: EventCategoryGetListRequestType) =>
    fetchAndCollate<EventCategoryType>(
      ({ page, pageSize }) =>
        restAPI.nodeAPI.GET<GetListResponseType<EventCategoryType>>({
          endpoint,
          query: {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_direction: sortDirection,
            ...filters,
          },
        }),
      page,
      pageSize
    ),

  /**
   * Create Event Category
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/event_categories/create_event_category
   */
  createOne: ({ data }: CreateOneRequestType<EventCategoryType>) =>
    restAPI.nodeAPI
      .POST<EventCategoryType>({ endpoint, body: data })
      .then((data) => ({ data })),

  /**
   * Update Event Category
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/event_categories/update_event_category
   */
  updateOne: ({ id, data }: UpdateOneRequestType<EventCategoryType>) =>
    restAPI.nodeAPI
      .PUT<EventCategoryType>({ endpoint: `${endpoint}/${id}`, body: data })
      .then((data) => ({ data })),

  /**
   * Delete Event Category
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/event_categories/delete_event_category
   */
  deleteOne: ({ id }: { id: string }) =>
    restAPI.nodeAPI.DELETE({ endpoint: `${endpoint}/${id}` }),
};

export const EventCategoryAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<EventCategoryAdapterType> =
  {
    getList: (params) => [Resources.EVENT_CATEGORY, "getList", params] as const,
    getOne: (params) =>
      [Resources.EVENT_CATEGORY, "getOne", params.id] as const,
    updateOne: () => [Resources.EVENT_CATEGORY] as const,
    createOne: () => [Resources.EVENT_CATEGORY] as const,
    deleteOne: () => [Resources.EVENT_CATEGORY] as const,
  };
